import { templateSearchablePolicyPanelComponent } from '../view/template';
import { BaseWebComponentController } from '../../../../../../framework/BaseWebComponentController';
import { store } from '@nirvana/redux/store';
import {
  AntimalwareOnExecuteSensitiveRegistryProtectionAction,
  DeviceControlRuleItem,
  FORMAT_TYPE_JSON,
  GeneralUpdateRing,
  Policy,
  PolicyCounters,
  SecurityTelemetrySiemType,
  UiSettings,
} from '@nirvana/shared/dtos/types/policies/common';
import { getPhantom } from '../../../redux/actions';
import { getSaveRestriction, setIsSaving } from '../../../redux/saveButtonSlice/actions.save';
import { PolicyPanelManager } from '../../../PolicyPanelManager';
import { GzToastNotificationWebComponent } from '../../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../../ui/gzToastNotification/type';
import { GzSearchablePolicyPanelWebComponent } from '../component/GzSearchablePolicyPanelWebComponent';
import { GzSearchablePolicyPanelRepository } from '../repository/GzSearchablePolicyPanelRepository';
import { GzSearchablePolicyPanelView } from '../view/GzSearchablePolicyPanelView';
import { PolicyPanelEvents } from '../../../utils/events';
import { INTEGRITY_MONITOR_REAL_TIME_DEFAULT_VALUE } from '../../../utils/uiSettings/integrityMonitor/integrity-monitor.values';
import { selectPoliciesState } from '../../../redux/slice';
import { PolicyContext } from '../../../redux/navigationHeaderSlice/types.navigation';
import { PATCH_MANAGEMENT_DEFAULT_VALUE } from '../../../utils/uiSettings/patchManagement/patch-management.values';
import {
  ANTIMALWARE_ANTI_EXPLOIT_DEFAULT_VALUE,
  ANTIMALWARE_ANTI_TAMPERING_DEFAULT_VALUE,
  ANTIMALWARE_DYNAMIC_THREAT_DEFENSE_DEFAULT_VALUE,
  ANTIMALWARE_EXCLUSIONS_DEFAULT_VALUE,
  ANTIMALWARE_ON_ACCESS_DEFAULT_VALUE,
  ANTIMALWARE_ON_DEMAND_DEFAULT_VALUE,
  ANTIMALWARE_SECURITY_SERVERS_DEFAULT_VALUE,
  getAntimalwareOnExecuteDefaultValue,
  getAntimalwareSettingsDefaultValue,
} from '../../../utils/uiSettings/antimalware/antimalware.values';
import {
  getNetworkSandboxingDefaultValue,
  GHOSTR_DEFAULT_VALUE,
  ICAP_DEFAULT_VALUE,
  SANDBOX_MANAGER_DEFAULT_VALUE,
} from '../../../utils/uiSettings/sandbox/sandbox.values';
import {
  FIREWALL_ADVANCED_DEFAULT_VALUE,
  FIREWALL_NETWORK_DEFAULT_VALUE,
  FIREWALL_SETTINGS_DEFAULT_VALUE,
} from '../../../utils/uiSettings/firewall/firewall.values';
import {
  CONTENT_CONTROL_DATA_PROTECTION_DEFAULT_VALUE,
  CONTENT_CONTROL_NETWORK_MONITOR_DEFAULT_VALUE,
  CONTENT_CONTROL_TRAFFIC_DEFAULT_VALUE,
  CONTENT_CONTROL_WEB_ACCESS_DEFAULT_VALUE,
} from '../../../utils/uiSettings/contentControl/content-control.values';
import {
  RELAY_COMMUNICATION_DEFAULT_VALUE,
  RELAY_UPDATE_DEFAULT_VALUE_FOR_CLOUD,
  RELAY_UPDATE_DEFAULT_VALUE_FOR_ONPREMISE,
} from '../../../utils/uiSettings/relay/relay.values';
import { EDR_SENSOR_GENERAL_DEFAULT_VALUE } from '../../../utils/uiSettings/edrSensor/edr-sensor.values';
import { ENCRYPTION_DEFAULT_VALUE } from '../../../utils/uiSettings/encryption/encryption.values';
import {
  STORAGE_PROTECTION_EXCLUSIONS_DEFAULT_VALUE,
  STORAGE_PROTECTION_ICAP_DEFAULT_VALUE,
} from '../../../utils/uiSettings/storageProtection/storage-protection.values';
import {
  getIndicatorsOfRiskDefaultValue,
  getPhasrDefaultValue,
} from '../../../utils/uiSettings/indicatorsOfRisk/indicators-of-risk.values';
import { LIVE_SEARCH_DEFAULT_VALUE } from '../../../utils/uiSettings/liveSearch/live-search.values';
import { APPLICATION_CONTROL_DEFAULT_VALUE } from '../../../utils/uiSettings/applicationControl/application-control.values';
import { NSX_DEFAULT_VALUE } from '../../../utils/uiSettings/nsx/nsx.values';
import {
  EXCHANGE_CONTENT_CONTROL_DEFAULT_VALUE,
  EXHANGE_ANTIMALWARE_DEFAULT_VALUE,
  getExchangeAntispamDefaultValue,
  getExchangeProtectionGeneralDefaultValue,
} from '../../../utils/uiSettings/exchange/exchange-protection.values';
import {
  getDeviceControlExceptionsDefaultValue,
  getDeviceControlRulesDefaultValue,
} from '../../../utils/uiSettings/deviceControl/device-control.values';
import deepmerge from 'deepmerge';
import _ from 'lodash';
import {
  GENERAL_UPDATE_DEFAULT_VALUE,
  getGeneralAdvancedDefaultValue,
  getGeneralCommunicationDefaultValue,
  getGeneralDetailsDefaultValue,
  getGeneralNotificationsDefaultValue,
  getGeneralSecurityTelemetryDefaultValue,
} from '../../../utils/uiSettings/general/general.values';
import { translateFn } from '../../../../../../utils/translator.helper';
import { GzDialogWebComponent } from '../../../../../../ui/gzDialog';
import { GzButtonWebComponent } from '../../../../../../ui/gzButton';
import { Feature } from '@nirvana/shared/constants';
import { PolicySaveRestriction } from '../../../redux/saveButtonSlice/types.save';
import { getBlocklistRulesDefaultValue } from '../../../utils/uiSettings/blocklistRules/blocklist-rules.values';

export class GzSearchablePolicyPanelController extends BaseWebComponentController {
  public component: GzSearchablePolicyPanelWebComponent;
  public dataRepository: GzSearchablePolicyPanelRepository;
  public view: GzSearchablePolicyPanelView;
  public viewConstructor: typeof GzSearchablePolicyPanelView;
  public dataRepositoryConstructor: typeof GzSearchablePolicyPanelRepository;

  constructor(component: GzSearchablePolicyPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: GzSearchablePolicyPanelView,
      viewTemplate: templateSearchablePolicyPanelComponent,
      dataRepositoryConstructor: GzSearchablePolicyPanelRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$sesarchableAccordionsView,
        event: PolicyPanelEvents.SAVE,
        callback: this.onSave,
      },
      {
        element: this.view.dom.$sesarchableAccordionsView,
        event: PolicyPanelEvents.CLONE,
        callback: this.onClone,
      },
      {
        element: this.view.dom.$sesarchableAccordionsView,
        event: PolicyPanelEvents.CANCEL,
        callback: this.onCancel,
      },
    ];
  }

  /**
   * Close the policy panel and reset callback.
   * @param navTo - navigate to a specific route after closing the policy panel.
   * @param shouldRedirect - should redirect to the previous route.
   */
  private closePolicyPanel(shouldRedirect = true, navTo?: string) {
    // remove hash change callback
    Nav.clearBeforeHashChangeCallback();

    // close the policy panel
    this.dependencies.api.clients.Policies.closePolicyPanel(shouldRedirect, navTo).toPromise();
  }

  /**
   * Check if navigation should continue in the current policy panel context.
   * @param oldUrl
   * @param newUrl
   */
  private shouldContinueNavigation(oldUrl: string, newUrl: string): boolean {
    // navigate to another route
    if (!newUrl.startsWith('/policies')) {
      return false;
    }

    // same create policy context
    const createPath = Application.Main.product.isCloud() ? '/policies/create/' : '/policies/endpoints/create/';
    if (oldUrl.startsWith(createPath) && newUrl.startsWith(createPath)) {
      return true;
    }

    // same clone/edit policy context
    const editPath = Application.Main.product.isCloud() ? '/policies/edit/' : '/policies/endpoints/edit/';
    const isEdit = oldUrl.startsWith(editPath) && newUrl.startsWith(editPath);

    const clonePath = Application.Main.product.isCloud() ? '/policies/clone/' : '/policies/endpoints/clone/';
    const isClone = oldUrl.startsWith(clonePath) && newUrl.startsWith(clonePath);

    const policyIdIndex = Application.Main.product.isCloud() ? 3 : 4;

    if (isEdit || isClone) {
      const oldPolicyId = oldUrl.split('/')[policyIdIndex];
      const newPolicyId = newUrl.split('/')[policyIdIndex];

      return oldPolicyId === newPolicyId;
    }

    return false;
  }

  /**
   * Handle hash change callback. Check if the user wants to discard changes before navigating to another route
   * @param oldUrl
   * @param newUrl
   */
  private onHashChangeCallback(oldUrl: string, newUrl: string) {
    // check if we should continue navigation (e.g. switch between tabs in the same policy panel)
    if (this.shouldContinueNavigation(oldUrl, newUrl)) {
      return true;
    }

    // cannot be saved, so we can close the policy panel directly
    const appState = selectPoliciesState(store.getState());
    const isRestricted = getSaveRestriction(appState) !== PolicySaveRestriction.NONE;
    if (isRestricted) {
      this.closePolicyPanel(false);
      return true;
    }

    // show dialog to confirm if the user wants to discard changes
    this.maybeClosePolicyPanel(newUrl);
    return false;
  }

  /**
   * Close policy panel and show dialog to confirm if the user wants to discard changes
   * @param navTo - navigate to this route after closing the policy panel
   */
  public maybeClosePolicyPanel(navTo?: string) {
    const windowBodyHtml = `
      <p>
        ${this.dependencies.translateFn('Changes you have made will be lost.')}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Discard')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Keep changes')}
      </gz-button>`;

    const windowTitle = this.dependencies.translateFn('Discard changes?');

    const dialog = GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, () => {
      dialog.close();
      this.closePolicyPanel(true, navTo);
    });
  }

  private onClone() {
    // remove hash change callback
    Nav.clearBeforeHashChangeCallback();

    // navigate to clone policy
    const path = Application.Main.product.isCloud() ? '/policies/clone/' : '/policies/endpoints/clone/';
    Nav.redirect(path + this.component.policyId);
  }

  private onCancel() {
    const appState = selectPoliciesState(store.getState());
    const isRestricted = getSaveRestriction(appState) !== PolicySaveRestriction.NONE;

    // cannot be saved, so we can close the policy panel directly
    if (isRestricted) {
      this.closePolicyPanel();
      return;
    }

    // show dialog to confirm if the user wants to discard changes
    this.maybeClosePolicyPanel();
  }

  private async onSave() {
    // send diff to backend
    const policyToSave = PolicyPanelManager.getInstance().getPolicyForSaving();
    this.beforeSave(policyToSave);

    // do nothing if policy has no inheritors
    if (!policyToSave?.inheritors || !policyToSave?.inheritors.length) {
      await this.savePolicy(policyToSave);
      return;
    }

    // show affected inheritors dialog
    const inheritorsNames = policyToSave.inheritors.map(inheritor => inheritor.name);
    const errorMessage =
      translateFn('The following policies inherit settings from this policy and may be modified:') +
      '<br/>' +
      '<ul><li>' +
      inheritorsNames.join('</li><li>') +
      '</li></ul>';

    const windowTitle = translateFn('Save policy?');
    const windowBodyHtml = `<div>${errorMessage}</div>`;
    const windowFooterHtml = `<gz-button id='confirm' class='mr-2'>
       ${translateFn('Save#preserve state')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        await this.savePolicy(policyToSave);

        dialog.close();
      }
    );
  }

  private async savePolicy(policyToSave: Policy | null) {
    store.dispatch(setIsSaving(true));

    const state = selectPoliciesState(store.getState());
    const phantom = getPhantom(state);
    const method = phantom
      ? this.dependencies.api.clients.Policies.createRecords
      : this.dependencies.api.clients.Policies.updateRecords;

    try {
      // Track PHASR analytics before saving
      PolicyPanelManager.getInstance().trackPhasrAnalyticsOnSave();
      
      await method([policyToSave]).toPromise();

      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn('Policy successfully saved.'),
        severity: ToastSeverity.SUCCESS,
      });
      this.closePolicyPanel(true);
    } catch (error) {
      let message = translateFn('Failed to save policy due to server error.');

      if (error.exceptionType === 'Exceptions\\NsxPolicyInUseException') {
        message = translateFn('This policy is used in NSX and cannot be disabled.');
      } else if (error.exceptionType === 'Exceptions\\NsxCouldNotConnectToNsxException') {
        message = translateFn('Could not connect to vCenter/NSX.');
      } else if (error.exceptionType === 'Exceptions\\NoIndicatorsOfRiskSelected') {
        message = translateFn('No risk indicators selected');
      }

      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: message,
        severity: ToastSeverity.ERROR,
      });
    } finally {
      store.dispatch(setIsSaving(false));
    }
  }

  private beforeSave(policy: Policy | null) {
    if (!policy) {
      return;
    }

    // fix nsx field before saving
    if (Application.Main.product.isCloud()) {
      _.unset(policy, ['uiSettings', 'nsx']);
    } else if (!_.get(policy, ['uiSettings', 'nsx', 'enabled'], false)) {
      _.set(policy, ['uiSettings', 'nsx', 'name'], null);
    }
  }

  async getDataForView(): Promise<{
    routingPath: string;
    policy: Policy | null;
    phantom: boolean;
    context: PolicyContext;
    counters?: PolicyCounters;
  }> {
    let policyData: Policy | null;
    let counters: PolicyCounters | undefined;
    const context = this.component.context;
    let phantom = true;

    switch (context) {
      case PolicyContext.ADD:
        policyData = await this.getNewRecord();
        break;
      case PolicyContext.CLONE:
        policyData = await this.getClonedRecord();
        break;
      case PolicyContext.EDIT:
        policyData = await this.dataRepository.getPolicyById(this.component.policyId);

        if (!Application.Main.product.isCloud()) {
          counters = await this.dataRepository.getCounters(this.component.policyId);
        }

        phantom = false;
        break;
    }

    // remove extra info
    this.normalizePolicyFormData(policyData);

    // merge with default values
    this.mergeWithDefaultValues(policyData);

    // set callback for hash change
    Nav.setBeforeHashChangeCallback(this.onHashChangeCallback, this);

    return {
      routingPath: this.component.routingPath,
      policy: policyData,
      phantom: phantom,
      context: this.component.context,
      counters: counters,
    };
  }

  private normalizePolicyFormData(policy: Policy) {
    const exclusionsLists = policy.uiSettings.antimalware.settings.activateExclusions.exclusionsLists || [];
    if (exclusionsLists.length) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'settings', 'activateExclusions', 'exclusionsLists'],
        exclusionsLists.map(exclusion => {
          if (_.get(exclusion, '_id')) {
            return _.get(exclusion, '_id');
          }

          return exclusion;
        })
      );
    }

    const selectedVendorExclusionLists =
      policy.uiSettings.antimalware.settings.activateExclusions.selectedVendorExclusionLists || [];
    if (selectedVendorExclusionLists.length) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'settings', 'activateExclusions', 'selectedVendorExclusionLists'],
        selectedVendorExclusionLists.map(exclusion => {
          if (_.get(exclusion, '_id')) {
            return _.get(exclusion, '_id');
          }

          return exclusion;
        })
      );
    }
    // the default policy comes without the created field so it should be initialized with an empty string
    if (policy.default) {
      policy.created = '';
    }

    // in case feature is not available with license, override disallowed value with slow ring
    const updateRing = policy.uiSettings.general.update.settings.updateRing || GeneralUpdateRing.SLOW_RING;
    if (
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE]) &&
      [GeneralUpdateRing.PRODUCTION_RING, GeneralUpdateRing.TEST1_RING, GeneralUpdateRing.TEST2_RING].includes(
        updateRing
      )
    ) {
      _.set(policy, ['uiSettings', 'general', 'update', 'settings', 'updateRing'], GeneralUpdateRing.SLOW_RING);
    }
    if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SECURITY_TELEMETRY_VISIBLE])) {
      const siemValue = policy.uiSettings.general.securityTelemetry.siem;
      if (siemValue === SecurityTelemetrySiemType.NONE) {
        _.set(policy, ['uiSettings', 'general', 'securityTelemetry', 'siem'], SecurityTelemetrySiemType.SPLUNK);
      }
      const formatValue = policy.uiSettings.general.securityTelemetry.format;
      if (formatValue === 0) {
        _.set(policy, ['uiSettings', 'general', 'securityTelemetry', 'format'], FORMAT_TYPE_JSON);
      }
    }
  }

  private async getClonedRecord(): Promise<Policy> {
    // create a template for policy
    const policy: Policy = {
      _id: '',
      name: '',
      default: false,
      myDefaultPolicy: false,
      ownerId: '',
      ownerUserName: '',
      ownerFullName: '',
      created: '',
      lastModified: '',
      service: 0,
      uiSettings: {} as UiSettings,
      company: null,
      clonedAfter: '',
      inheritance: [],
      inheritors: [],
      readOnly: false,
      noOwner: false,
    };
    // get old record
    const oldRecord = await this.dataRepository.getPolicyById(this.component.policyId);

    // set owner full name
    policy.ownerFullName = Application.Main.user.profile.fullName;

    // set name
    policy.name = await this.dataRepository.generateSuggestedCloneName(oldRecord.name);

    // copy from old record
    policy.service = oldRecord.service;
    policy.clonedAfter = oldRecord._id;
    policy.uiSettings = oldRecord.uiSettings;
    policy.inheritance = oldRecord.inheritance || [];

    if (policy.uiSettings['nsx']) {
      // force/ensure nsx default flag to false
      policy.uiSettings['nsx']['default'] = false;

      // remove nsx name to force regenerate
      policy.uiSettings['nsx']['name'] = '';
    }

    if (oldRecord.company?.id !== Application.Main.user.company.id) {
      // When cloning policy of another company, reset Integrity Monitoring section to default values, because
      // rule sets belong only to one specific company, so they should not be cloned from other companies
      policy.uiSettings.integrityMonitor.realTime = INTEGRITY_MONITOR_REAL_TIME_DEFAULT_VALUE;
    }

    return policy;
  }

  private async getNewRecord(): Promise<Policy> {
    const policy = await this.dataRepository.getDefaultPolicy();

    // set the technical information according to my language
    policy.uiSettings.general.display.supportInformation = {
      website: 'https://www.bitdefender.com/business/customer-portal/enterprise-standard-support.html',
      email: 'https://www.bitdefender.com/support/contact-us.html',
      phone: '(+1) 954 414 9621',
    };

    // reset nsx policy name in order to force regenerate
    if (policy.uiSettings.nsx) {
      policy['uiSettings']['nsx']['default'] = false;
      policy['uiSettings']['nsx']['name'] = '';
    }

    if (!policy.company || policy.company.id !== Application.Main.user.company.id) {
      // When creating policy from a default policy owned by another company, reset Integrity Monitoring section
      // because rule sets belong only to one specific company, so they should not be cloned from other companies
      policy.uiSettings.integrityMonitor.realTime = INTEGRITY_MONITOR_REAL_TIME_DEFAULT_VALUE;
    }

    // If the company has MDR active, sensitiveRegistryProtection should be enabled and set to 'kill' by default
    if (
      (!policy.company || policy.company.id !== Application.Main.user.company.id) &&
      Application.Main.checkIfCompanyHasMDR(Application.Main.user.companyInfo)
    ) {
      policy.uiSettings.antimalware.onExecute.sensitiveRegistryProtection = {
        enable: true,
        action: AntimalwareOnExecuteSensitiveRegistryProtectionAction.KILL_PROCESS,
      };
    }

    policy.created = new Date().toString();
    policy.lastModified = '';
    policy.ownerFullName = Application.Main.user.profile.fullName;
    policy.ownerId = Application.Main.user._id;
    policy.inheritance = [];

    // set name
    policy.name = await this.dataRepository.getSuggestedNewPolicyName();

    return policy;
  }

  private mergeWithDefaultValues(policy: Policy): void {
    const policyManager = PolicyPanelManager.getInstance();

    // General section
    _.set(
      policy,
      ['uiSettings', 'general', 'communication'],
      deepmerge(getGeneralCommunicationDefaultValue(), policy.uiSettings.general?.communication || {})
    );
    _.set(
      policy,
      ['uiSettings', 'general', 'display', 'silentMode'],
      deepmerge(
        {
          ...getGeneralNotificationsDefaultValue().uiSettings.general.display.silentMode,
          ...CONTENT_CONTROL_TRAFFIC_DEFAULT_VALUE.uiSettings.general.display.silentMode,
        },
        policy.uiSettings.general.display.silentMode || {}
      )
    );
    _.set(
      policy,
      ['uiSettings', 'general', 'update'],
      deepmerge(GENERAL_UPDATE_DEFAULT_VALUE.uiSettings.general.update, policy.uiSettings.general.update || {})
    );
    _.set(
      policy,
      ['uiSettings', 'general', 'advanced'],
      deepmerge(
        {
          ...getGeneralAdvancedDefaultValue().uiSettings.general.advanced,
          ...CONTENT_CONTROL_TRAFFIC_DEFAULT_VALUE.uiSettings.general.advanced,
        },
        policy.uiSettings.general.advanced || {}
      )
    );
    if (policyManager.isGeneralSecurityTelemetryAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'general', 'securityTelemetry'],
        deepmerge(getGeneralSecurityTelemetryDefaultValue(), policy.uiSettings.general?.securityTelemetry || {})
      );
    }

    // details section
    _.set(
      policy,
      ['uiSettings', 'general', 'allowChangeByOtherUsers'],
      policy.uiSettings.general?.allowChangeByOtherUsers
    );
    _.set(
      policy,
      ['uiSettings', 'general', 'display'],
      deepmerge(getGeneralDetailsDefaultValue().uiSettings.general.display, policy.uiSettings.general?.display)
    );

    _.set(
      policy,
      ['uiSettings', 'antimalware', 'onExecute'],
      deepmerge(getAntimalwareOnExecuteDefaultValue(), policy.uiSettings.antimalware?.onExecute || {})
    );

    if (policyManager.isAntimalwareSettingsSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'settings'],
        deepmerge(
          getAntimalwareSettingsDefaultValue().uiSettings.antimalware.settings,
          policy.uiSettings.antimalware?.settings || {}
        )
      );
    }

    _.set(
      policy,
      ['uiSettings', 'antimalware', 'settings'],
      deepmerge(
        ANTIMALWARE_EXCLUSIONS_DEFAULT_VALUE.uiSettings.antimalware.settings,
        policy.uiSettings.antimalware?.settings || {}
      )
    );

    if (policyManager.isAntimalwareOnAccessSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'onAccess'],
        deepmerge(ANTIMALWARE_ON_ACCESS_DEFAULT_VALUE, policy.uiSettings.antimalware?.onAccess || {})
      );
    }
    if (policyManager.isAntimalwareOnDemandSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'onDemand'],
        deepmerge(ANTIMALWARE_ON_DEMAND_DEFAULT_VALUE, policy.uiSettings.antimalware?.onDemand || {})
      );
    }
    if (policyManager.isAntimalwareAntiTamperingSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'antiTampering'],
        deepmerge(ANTIMALWARE_ANTI_TAMPERING_DEFAULT_VALUE, policy.uiSettings.antimalware?.antiTampering || {})
      );
    }
    if (policyManager.isAntimalwareDynamicThreatDefenseSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'dynamicThreatDefense'],
        deepmerge(
          ANTIMALWARE_DYNAMIC_THREAT_DEFENSE_DEFAULT_VALUE,
          policy.uiSettings.antimalware?.dynamicThreatDefense || {}
        )
      );
    }
    if (policyManager.isAntimalwareSecurityServersSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'securityServers'],
        deepmerge(ANTIMALWARE_SECURITY_SERVERS_DEFAULT_VALUE, policy.uiSettings.antimalware?.securityServers || {})
      );
    }
    if (policyManager.isAntimalwareAntiExploitSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'antimalware', 'antiExploit'],
        deepmerge(ANTIMALWARE_ANTI_EXPLOIT_DEFAULT_VALUE, policy.uiSettings.antimalware?.antiExploit || {})
      );
    }

    const isEndpointSensorAvailable = policyManager.isSandboxEndpointSensorSectionAvailable();
    const isNetworkSensorAvailable = policyManager.isSandboxNetworkSensorSectionAvailable();
    const isIcapSensorAvailable = policyManager.isSandboxIcapSensorSectionAvailable();
    const isManagerAvailable = policyManager.isSandboxManagerSectionAvailable();
    const isSandboxAvailable =
      isEndpointSensorAvailable || isNetworkSensorAvailable || isIcapSensorAvailable || isManagerAvailable;
    if (isSandboxAvailable) {
      if (isEndpointSensorAvailable) {
        _.set(
          policy,
          ['uiSettings', 'networkSandboxing'],
          deepmerge(getNetworkSandboxingDefaultValue(), policy.uiSettings.networkSandboxing || {})
        );
      }
      if (isIcapSensorAvailable) {
        _.set(policy, ['uiSettings', 'icapSensor'], deepmerge(ICAP_DEFAULT_VALUE, policy.uiSettings.icapSensor || {}));
      }
      if (isNetworkSensorAvailable) {
        _.set(policy, ['uiSettings', 'ghostr'], deepmerge(GHOSTR_DEFAULT_VALUE, policy.uiSettings.ghostr || {}));
      }
      if (isManagerAvailable) {
        _.set(
          policy,
          ['uiSettings', 'sandboxManager'],
          deepmerge(SANDBOX_MANAGER_DEFAULT_VALUE, policy.uiSettings.sandboxManager || {})
        );
      }
    }

    if (policyManager.isFirewallModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'firewall', 'advanced'],
        deepmerge(FIREWALL_ADVANCED_DEFAULT_VALUE, policy.uiSettings.firewall?.advanced || {})
      );

      // fix rules if needed
      policy.uiSettings.firewall.advanced.rule = policy.uiSettings.firewall.advanced.rule.map(rule => {
        return {
          ...rule,
          enabled: typeof rule.enabled === 'boolean' ? rule.enabled : true,
        };
      });

      _.set(
        policy,
        ['uiSettings', 'firewall', 'network'],
        deepmerge(FIREWALL_NETWORK_DEFAULT_VALUE, policy.uiSettings.firewall?.network || {})
      );
      _.set(
        policy,
        ['uiSettings', 'firewall', 'settings'],
        deepmerge(FIREWALL_SETTINGS_DEFAULT_VALUE, policy.uiSettings.firewall?.settings || {})
      );
    }

    _.set(
      policy,
      ['uiSettings', 'contentControl', 'antiphishing'],
      deepmerge(
        CONTENT_CONTROL_DATA_PROTECTION_DEFAULT_VALUE.uiSettings.contentControl.antiphishing,
        policy.uiSettings.contentControl?.antiphishing || {}
      )
    );
    _.set(
      policy,
      ['uiSettings', 'contentControl', 'traffic'],
      deepmerge(
        {
          ...CONTENT_CONTROL_TRAFFIC_DEFAULT_VALUE.uiSettings.contentControl.traffic,
          ...CONTENT_CONTROL_DATA_PROTECTION_DEFAULT_VALUE.uiSettings.contentControl.traffic,
        },
        policy.uiSettings.contentControl?.traffic || {}
      )
    );

    if (policyManager.isContentControlNetworkSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'contentControl', 'networkMonitor'],
        deepmerge(CONTENT_CONTROL_NETWORK_MONITOR_DEFAULT_VALUE, policy.uiSettings.contentControl?.networkMonitor || {})
      );
    }
    if (policyManager.isContentControlWebSectionAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'contentControl', 'webAccess'],
        deepmerge(
          CONTENT_CONTROL_WEB_ACCESS_DEFAULT_VALUE.uiSettings.contentControl.webAccess,
          policy.uiSettings.contentControl?.webAccess || {}
        )
      );
      _.set(
        policy,
        ['uiSettings', 'contentControl', 'webCategoriesFilter'],
        deepmerge(
          CONTENT_CONTROL_WEB_ACCESS_DEFAULT_VALUE.uiSettings.contentControl.webCategoriesFilter,
          policy.uiSettings.contentControl?.webCategoriesFilter || {}
        )
      );
      _.set(
        policy,
        ['uiSettings', 'contentControl', 'application'],
        deepmerge(
          CONTENT_CONTROL_WEB_ACCESS_DEFAULT_VALUE.uiSettings.contentControl.application,
          policy.uiSettings.contentControl?.application || {}
        )
      );
      _.set(
        policy,
        ['uiSettings', 'contentControl', 'dataProtection'],
        deepmerge(
          CONTENT_CONTROL_WEB_ACCESS_DEFAULT_VALUE.uiSettings.contentControl.dataProtection,
          policy.uiSettings.contentControl?.dataProtection || {}
        )
      );
    }

    if (policyManager.isPatchManagementModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'patchManagement'],
        deepmerge(PATCH_MANAGEMENT_DEFAULT_VALUE, policy.uiSettings.patchManagement || {})
      );
    }

    if (policyManager.isIntegrityMonitorModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'integrityMonitor', 'realTime'],
        deepmerge(INTEGRITY_MONITOR_REAL_TIME_DEFAULT_VALUE, policy.uiSettings.integrityMonitor?.realTime || {})
      );
    }

    _.set(
      policy,
      ['uiSettings', 'relay', 'communication'],
      deepmerge(RELAY_COMMUNICATION_DEFAULT_VALUE, policy.uiSettings.relay?.communication || {})
    );
    _.set(
      policy,
      ['uiSettings', 'relay', 'update'],
      deepmerge(
        Application.Main.product.isCloud()
          ? RELAY_UPDATE_DEFAULT_VALUE_FOR_CLOUD
          : RELAY_UPDATE_DEFAULT_VALUE_FOR_ONPREMISE,
        policy.uiSettings.relay?.update || {}
      )
    );

    if (policyManager.isEdrSensorModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'edrSensor', 'general'],
        deepmerge(EDR_SENSOR_GENERAL_DEFAULT_VALUE, policy.uiSettings.edrSensor?.general || {})
      );
    }

    if (policyManager.isEncryptionModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'encryption'],
        deepmerge(ENCRYPTION_DEFAULT_VALUE, policy.uiSettings.encryption || {})
      );
    }

    if (policyManager.isDeviceControlModuleAvailable()) {
      const overwriteMerge = (
        destinationArray: DeviceControlRuleItem[],
        sourceArray: DeviceControlRuleItem[],
        options
      ) => {
        return sourceArray.map((sourceItem, index) => {
          return deepmerge(destinationArray[index], sourceItem, options);
        });
      };

      _.set(
        policy,
        ['uiSettings', 'deviceControl', 'dataLossPrevention'],
        deepmerge(getDeviceControlRulesDefaultValue(), policy.uiSettings.deviceControl?.dataLossPrevention || {}, {
          arrayMerge: overwriteMerge,
        })
      );
      _.set(
        policy,
        ['uiSettings', 'deviceControl', 'dataLossPreventionExceptions'],
        deepmerge(
          getDeviceControlExceptionsDefaultValue(),
          policy.uiSettings.deviceControl?.dataLossPreventionExceptions || {}
        )
      );
    }

    if (policyManager.isStorageProtectionModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'storageProtection', 'icap'],
        deepmerge(STORAGE_PROTECTION_ICAP_DEFAULT_VALUE, policy.uiSettings.storageProtection?.icap || {})
      );
      _.set(
        policy,
        ['uiSettings', 'storageProtection', 'exclusions'],
        deepmerge(STORAGE_PROTECTION_EXCLUSIONS_DEFAULT_VALUE, policy.uiSettings.storageProtection?.exclusions || {})
      );
    }

    if (policyManager.isIndicatorsOfRiskModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'indicatorsOfRisk'],
        deepmerge(getIndicatorsOfRiskDefaultValue(), policy.uiSettings.indicatorsOfRisk || {})
      );
      _.set(policy, ['uiSettings', 'phasr'], deepmerge(getPhasrDefaultValue(), policy.uiSettings.phasr || {}));
    }

    if (policyManager.isLiveSearchModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'liveSearch'],
        deepmerge(LIVE_SEARCH_DEFAULT_VALUE, policy.uiSettings.liveSearch || {})
      );
    }

    if (policyManager.isApplicationControlModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'applicationControl'],
        deepmerge(APPLICATION_CONTROL_DEFAULT_VALUE, policy.uiSettings.applicationControl || {})
      );
    }

    if (policyManager.isNsxModuleAvailable()) {
      _.set(policy, ['uiSettings', 'nsx'], deepmerge(NSX_DEFAULT_VALUE, policy.uiSettings.nsx || {}));
    }

    if (policyManager.isBlocklistModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'blocklistRules'],
        deepmerge(getBlocklistRulesDefaultValue, policy.uiSettings.blocklistRules || {})
      );
    }

    if (policyManager.isExchangeModuleAvailable()) {
      _.set(
        policy,
        ['uiSettings', 'exchange'],
        deepmerge(getExchangeProtectionGeneralDefaultValue(), policy.uiSettings.exchange || {})
      );
      _.set(
        policy,
        ['uiSettings', 'exchange', 'antimalware'],
        deepmerge(EXHANGE_ANTIMALWARE_DEFAULT_VALUE, policy.uiSettings.exchange?.antimalware || {})
      );
      _.set(
        policy,
        ['uiSettings', 'exchange', 'antispam'],
        deepmerge(getExchangeAntispamDefaultValue(), policy.uiSettings.exchange?.antispam || {})
      );
      _.set(
        policy,
        ['uiSettings', 'exchange', 'contentControl'],
        deepmerge(EXCHANGE_CONTENT_CONTROL_DEFAULT_VALUE, policy.uiSettings.exchange?.contentControl || {})
      );
    }
  }
}
